'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import CorrectionNote from '@/app/components/blog/CorrectionNote'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Tap Water vs Bottled Water: Which Has More Microplastics?"
      author="MicroPlastics Editorial Team"
      date="May 25, 2026"
      readTime="12 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Tap water vs bottled water microplastics, which has more"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This page previously described NSF P473 as a microplastics certification and recommended Clearly Filtered and Epic as certified choices. P473 covered PFOA/PFOS only; NSF/ANSI 401 is the only certification with a microplastics claim, and Clearly Filtered and Epic are lab-tested, not certified, for it. It also credited reverse osmosis and Berkey with high microplastic removal that most models are not certified for and Berkey has not tested.
      </CorrectionNote>

      <p className="text-lg text-gray-700 leading-relaxed">
        Most people buy bottled water assuming it&apos;s safer than what
        comes out of the tap. For microplastics, the math usually goes the
        other way. A 2024 PNAS study counted about 240,000 plastic
        particles per liter in bottled water, most of them nanoplastics
        small enough to slip into blood. Unfiltered tap water has
        microplastics too, but typically orders of magnitude fewer. The
        cheaper option is usually the cleaner option, as long as you
        filter and drink from glass or stainless.
      </p>

      <BlogTLDR>
        Bottled water averages <strong>~240,000 plastic particles per
        liter</strong> (PNAS, Qian 2024). Unfiltered municipal tap is
        typically <strong>a few to a few hundred particles per
        liter</strong> in published surveys. A good activated carbon block
        or reverse osmosis filter removes <strong>80–99% of
        microplastics</strong> from tap. Net order: filtered tap in glass
        or stainless &gt; unfiltered tap &gt; bottled in glass &gt;
        bottled in PET. Check your local water quality report and your
        home plumbing before going filter-free.
      </BlogTLDR>

      <BlogComparisonTable
        caption="Microplastic load by drinking water source"
        columns={[
          { key: 'source', label: 'Source' },
          { key: 'particles', label: 'Typical particles per liter' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { source: 'Filtered tap (reverse osmosis)', particles: 'Model-dependent: AquaTru is certified at 96.48% (0.5–1 µm); many RO units have no microplastics claim', verdict: 'Check the certification' },
          { source: 'Filtered tap (NSF/ANSI 401 certified filter)', particles: 'Certified 96–99.8% reduction of 0.5–1 µm particles', verdict: 'Best cost-to-performance' },
          { source: 'Glass-bottled spring water', particles: 'Low, mainly cap-side release', verdict: 'Better than PET; expensive' },
          { source: 'Unfiltered municipal tap', particles: 'A few to a few hundred (Mintenig 2019; varies by region)', verdict: 'Acceptable; quality varies' },
          { source: 'Single-use PET bottled water', particles: '~240,000 (Qian 2024, ~90% nanoplastics)', verdict: 'Highest daily exposure' },
        ]}
      />

      <BlogKeyTakeaways
        items={[
          <>Filtered tap in glass or stainless is the lowest-microplastic everyday choice for most US households.</>,
          <>Unfiltered tap still beats PET bottled water on microplastic load in most published comparisons.</>,
          <>The 2024 nanoplastic counting method finally revealed how much PET-bottled water was being undercounted.</>,
          <>Tap water quality varies by region, system age, and home plumbing, a local water report tells you what your tap actually contains.</>,
          <>Look for NSF/ANSI 401 with a microplastics claim on the filter you buy: it is the only certification that covers microplastics.</>,
          <>Old fridge filters and basic pitchers can underperform, replace cartridges on schedule.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why bottled water usually loses
      </h2>
      <p>
        Bottled water is not contaminated at the spring. It picks up
        plastic from the package and the journey:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>PET hydrolysis</strong>, slow polymer breakdown that accelerates with heat, UV, and time on shelf.</li>
        <li><strong>Cap friction</strong>. HDPE caps grind against bottle threads in shipping and every open-close cycle.</li>
        <li><strong>Acidic / sparkling SKUs</strong>, lower pH speeds plasticiser migration.</li>
        <li><strong>Long storage at warm temperatures</strong>, warehouse → truck → garage stacks the damage.</li>
      </ul>
      <p>
        See the full breakdown in our{' '}
        <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          bottled water microplastics
        </Link>{' '}
        guide.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        When tap water is not automatically safer
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Old lead service lines.</strong> Microplastics aren&apos;t the only issue, lead is the bigger one. Check your service line; some utilities offer free testing.</li>
        <li><strong>Aging municipal infrastructure.</strong> Treatment plants vary widely in particulate removal. Larger metros often outperform small rural systems.</li>
        <li><strong>Private well water.</strong> Not regulated. Get an annual independent test, including for microplastic if available.</li>
        <li><strong>Boil-water advisories.</strong> Different problem (bacteria), but a sign your system is under stress.</li>
        <li><strong>Visible sediment or smell.</strong> Don&apos;t guess, test.</li>
      </ul>
      <p>
        Every US municipal water customer can request the annual{' '}
        <strong>Consumer Confidence Report (CCR)</strong>, it lists what
        was detected and at what concentration. If you&apos;re on a well,
        you need an independent lab test.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Filters that actually reduce microplastics
      </h2>
      <BlogComparisonTable
        caption="Home filters for microplastic reduction (verify current model specs)"
        columns={[
          { key: 'type', label: 'Filter type' },
          { key: 'examples', label: 'Examples' },
          { key: 'mp', label: 'Microplastic reduction' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { type: 'Reverse osmosis (countertop or under-sink)', examples: 'AquaTru, Waterdrop G3, APEC', mp: 'AquaTru 96.48% certified; Waterdrop and APEC not certified for microplastics', note: 'Strongest for dissolved contaminants; check the model for microplastics' },
          { type: 'Certified pitcher or countertop (NSF/ANSI 401)', examples: 'PUR PLUS, Brita Elite, LifeStraw Home glass, Aquasana Clean Water Machine', mp: '96–99.8% certified', note: 'Easy install; replace cartridges on schedule' },
          { type: 'Lab-tested but not certified', examples: 'Clearly Filtered, Epic Pure', mp: 'Brand lab results', note: 'No microplastics certification; Berkey says it has not tested at all' },
          { type: 'Standard pitcher (no 401 claim)', examples: 'Brita Standard and similar', mp: 'Not certified', note: 'Taste and chlorine; not a microplastic filter' },
          { type: 'Fridge filter', examples: 'OEM ice/water filters', mp: 'Varies, usually modest', note: 'Replace on schedule; not a microplastic primary' },
        ]}
      />
      <p>
        See <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">water filters compared</Link> for the full brand-by-brand ranking.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about boiling, freezing, or letting water sit?
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Boiling.</strong> A 2024 study (Yu et al.) found that boiling hard tap water for 5 minutes can reduce nano- and microplastic levels by up to ~80%, with calcium carbonate co-precipitating particles. Soft water shows less benefit. Boiling is a useful supplement, not a replacement for filtration.</li>
        <li><strong>Freezing.</strong> No meaningful microplastic reduction in published evidence.</li>
        <li><strong>Letting water sit.</strong> No effect on particles; can help with chlorine smell.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Country-by-country differences matter
      </h2>
      <p>
        Where you live changes the answer. Different countries have very
        different baseline microplastic loads in tap water due to source
        water quality, treatment plant standards, and pipe materials. See
        our most-read guide:{' '}
        <Link href="/blog/microplastics-in-drinking-water-by-country" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in drinking water by country
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What to do today
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Pull your CCR</strong> (municipal customers) or order a well test. Find out what you&apos;re actually drinking.</li>
        <li><strong>Install an NSF-certified filter</strong>, carbon block (pitcher or under-sink) for most homes, RO if you want the maximum.</li>
        <li><strong>Replace cartridges on schedule</strong>, overdue cartridges underperform.</li>
        <li><strong>Refill into glass or stainless</strong>, never single-use plastic for repeat use.</li>
        <li><strong>If you must buy bottled,</strong> glass-bottled or aluminum-canned, never stored in heat.</li>
        <li><strong>Scan bottled drinks</strong> on the way to the cart, the app weighs material, condition, and brand.</li>
      </ol>

      <BlogWhatTheAppChecks
        items={[
          <>Bottled drink material. PET, HDPE, glass, aluminum, multi-layer carton.</>,
          <>Container condition cues from the photo, dents, scratches, label wear.</>,
          <>Brand, product category, and SKU variant, sparkling vs still vs flavored.</>,
          <>Context you log, heat exposure, reuse, storage time.</>,
          <>Linked published studies behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan before you stock the fridge"
        sub="Tap the barcode on any bottled drink. The app weighs packaging, brand, and condition, then gives you a 0–100 risk score and a safer swap if it scores high."
        buttonLabel="Scan bottled drinks in the app"
      />

      <p>
        Related reading:{' '}
        <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          bottled water microplastics
        </Link>,{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          water filters compared
        </Link>,{' '}
        <Link href="/blog/microplastics-in-drinking-water-by-country" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          drinking water by country
        </Link>,{' '}
        <Link href="/blog/best-stainless-steel-water-bottles" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best stainless steel water bottles
        </Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Is tap water safer than bottled water for microplastics?',
            answer:
              'Usually yes. Unfiltered municipal tap water typically contains a few to a few hundred plastic particles per liter, vs ~240,000 particles per liter in PET bottled water (Qian 2024, PNAS). Filtered tap in glass or stainless is the lowest-microplastic everyday choice for most US households.',
          },
          {
            question: 'What is the safest water to drink for microplastics?',
            answer:
              'Filtered tap from a filter certified to NSF/ANSI 401 for microplastics, served in a glass or stainless steel bottle. Certified pitchers (PUR PLUS, Brita Elite, LifeStraw Home glass) are the best cost-to-performance option; reverse osmosis is strongest for dissolved contaminants such as PFAS, but only some RO models, such as AquaTru, are certified for microplastics.',
          },
          {
            question: 'Do basic Brita or Pur pitchers remove microplastics?',
            answer:
              'Basic pitchers reduce some microplastics through their carbon filter, but they are not microplastic-targeted. For a certified reduction, choose a filter certified to NSF/ANSI 401 for microplastics, such as the PUR PLUS or the Brita Elite cartridge. Clearly Filtered and Epic publish lab results but are not certified for microplastics.',
          },
          {
            question: 'Does boiling tap water remove microplastics?',
            answer:
              'A 2024 study by Yu et al. found that boiling hard tap water for 5 minutes can reduce nano- and microplastic levels by up to ~80%, calcium carbonate co-precipitates the particles. Soft water shows less benefit. Boiling is a useful supplement to filtration, not a full replacement.',
          },
          {
            question: 'Should I install a whole-house filter?',
            answer:
              'For microplastic reduction at the tap, a point-of-use carbon block (kitchen sink) or RO is usually enough and far cheaper. Whole-house systems make sense if you have hard water or want to reduce chlorine in showers and laundry, but the drinking-water point is where particle reduction matters most.',
          },
          {
            question: 'Is well water more or less likely to have microplastics?',
            answer:
              'Well water has no municipal treatment, so anything in the groundwater stays. Studies have found microplastics in shallow wells near plastic-rich soils. Get an annual independent lab test; consider RO if you want to minimize microplastic and other contaminants like nitrate or pesticides.',
          },
          {
            question: 'I live in a country with poor tap water. What now?',
            answer:
              'If municipal tap is unsafe to drink for biological reasons, microplastics are a secondary concern. Use a multi-stage filter (sediment + carbon + RO) and follow local boiling guidance. Glass-bottled water or aluminum-canned water is the bottled fallback. See our drinking water by country guide for region-specific notes.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'PNAS',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Mintenig SM, Löder MGJ, Primpke S, Gerdts G',
            year: 2019,
            title: 'Low numbers of microplastics detected in drinking water from ground water sources',
            publisher: 'Science of the Total Environment',
            url: 'https://pubmed.ncbi.nlm.nih.gov/30342357/',
          },
          {
            authors: 'Yu Z, Jiang Q, Wu W-M, et al.',
            year: 2024,
            title: 'Drinking Boiled Tap Water Reduces Human Intake of Nanoplastics and Microplastics',
            publisher: 'Environmental Science & Technology Letters',
            url: 'https://pubs.acs.org/doi/10.1021/acs.estlett.4c00081',
          },
          {
            authors: 'Eerkes-Medrano D, Leslie HA, Quinn B',
            year: 2019,
            title: 'Microplastics in drinking water: a review and assessment',
            publisher: 'Current Opinion in Environmental Science & Health',
            url: 'https://www.sciencedirect.com/science/article/pii/S2468584418300734',
          },
          {
            authors: 'NSF International',
            year: 2024,
            title: 'NSF Protocol P473: PFOA/PFOS reduction certification',
            publisher: 'NSF',
            url: 'https://www.nsf.org/consumer-resources/water-quality/water-filters-treatment',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
